import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { GroceryCard } from "@/components/GroceryCard";
import { Input } from "@/components/ui/input";
import { Search, ShoppingBasket, LayoutGrid } from "lucide-react";

function RailItem({ label, imgId, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex w-full flex-col items-center gap-1 rounded-xl px-1 py-2 text-center transition-colors ${active ? "bg-brand/15" : "hover:bg-cream-alt"}`}
    >
      <span className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-full ring-2 transition-all ${active ? "ring-brand" : "ring-transparent"}`}>
        {imgId ? (
          <img src={fileUrl(imgId)} alt={label} className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center bg-brand/10 text-brand"><LayoutGrid className="h-6 w-6" /></span>
        )}
      </span>
      <span className={`text-[11px] font-semibold leading-tight ${active ? "text-ink" : "text-ink-soft"}`}>{label}</span>
    </button>
  );
}

function ChipItem({ label, active, onClick, small, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 whitespace-nowrap rounded-full border font-semibold transition-colors ${small ? "px-3.5 py-1 text-xs" : "px-4 py-1.5 text-sm"} ${active ? "border-brand bg-brand text-ink" : "border-border bg-white text-ink-soft hover:border-brand"}`}
    >
      {label}
    </button>
  );
}

export default function Products() {
  usePageMeta("Shop All Products", "Browse groceries, fruits, vegetables, dairy and daily essentials from Nicee with Cash on Delivery.");
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [activeCat, setActiveCat] = useState(searchParams.get("category") || "");
  const [activeSub, setActiveSub] = useState(searchParams.get("subcategory") || "");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setActiveCat(searchParams.get("category") || "");
    setActiveSub(searchParams.get("subcategory") || "");
    setSearch(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    if (activeCat) {
      api.get("/subcategories", { params: { category_id: activeCat } }).then(({ data }) => setSubcats(data)).catch(() => setSubcats([]));
    } else {
      setSubcats([]);
    }
  }, [activeCat]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat) params.category_id = activeCat;
    if (activeSub) params.subcategory_id = activeSub;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/products", { params }).then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [activeCat, activeSub, search]);

  const setParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setSearchParams(next, { replace: true });
  };
  const onSearchChange = (v) => { setSearch(v); setParams({ search: v }); };
  const onCategory = (id) => { setActiveCat(id); setActiveSub(""); setParams({ category: id, subcategory: "" }); };
  const onSub = (id) => { setActiveSub(id); setParams({ subcategory: id }); };

  const activeCatName = categories.find((c) => c.id === activeCat)?.name;
  const heading = search ? `Results for "${search}"` : activeCatName || "All Products";

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">{heading}</h1>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search for products..." data-testid="product-search" className="rounded-full pl-9" />
        </div>
      </div>

      <div className="mt-5 flex gap-4 lg:gap-6">
        {/* Desktop vertical category rail */}
        <aside className="hidden shrink-0 lg:block" data-testid="category-rail">
          <div className="sticky top-20 flex max-h-[calc(100vh-6rem)] w-24 flex-col gap-1 overflow-y-auto no-scrollbar pr-1">
            <RailItem label="All" active={!activeCat} onClick={() => onCategory("")} />
            {categories.map((c) => (
              <RailItem key={c.id} label={c.name} imgId={c.image_id} active={activeCat === c.id} onClick={() => onCategory(c.id)} testid={`rail-${c.slug}`} />
            ))}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* Mobile horizontal category rail */}
          <div className="mb-4 flex gap-2 overflow-x-auto no-scrollbar lg:hidden" data-testid="category-rail-mobile">
            <ChipItem label="All" active={!activeCat} onClick={() => onCategory("")} />
            {categories.map((c) => <ChipItem key={c.id} label={c.name} active={activeCat === c.id} onClick={() => onCategory(c.id)} testid={`chip-${c.slug}`} />)}
          </div>

          {/* Sub-category chips */}
          {subcats.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="subcategory-chips">
              <ChipItem label="All" active={!activeSub} onClick={() => onSub("")} small />
              {subcats.map((s) => <ChipItem key={s.id} label={s.name} active={activeSub === s.id} onClick={() => onSub(s.id)} small testid={`subcat-${s.slug || s.id}`} />)}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => <div key={i} className="h-60 animate-pulse rounded-2xl bg-cream-alt" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center" data-testid="products-empty">
              <ShoppingBasket className="mx-auto h-10 w-10 text-ink-muted" />
              <p className="mt-4 font-heading text-lg text-ink">No products found</p>
              <p className="mt-1 text-sm text-ink-muted">{search ? "Try a different search term." : "New stock is added regularly — please check back soon."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4" data-testid="products-grid">
              {products.map((p) => <GroceryCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
